/**
 * Engine Store — Zustand-based state management.
 *
 * Single source of truth for the 3D scene state.
 * External consumers access state via getSnapshot() — never import this store directly.
 *
 * @see docs/ims-brand-frontends/04-state-management.md
 */

import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import type {
  EngineError,
  EngineStateSnapshot,
  LoadingPhase,
} from '../api/types';
import type { ResolvedColorDefinition } from '@ims/shared';

// ---------------------------------------------------------------------------
// Store Shape
// ---------------------------------------------------------------------------

export interface EngineStoreState {
  // --- Loading ---
  loading: boolean;
  loadingProgress: number;
  loadingPhase: LoadingPhase;
  isReady: boolean;

  // --- Error ---
  lastError: EngineError | null;

  // --- Camera ---
  autoRotating: boolean;

  // --- Memory ---
  memoryUsageMB: number;

  // --- Material / Colors ---
  availableColors: ResolvedColorDefinition[];
  activeColorId: string | null;

  // --- Actions ---
  setLoading: (loading: boolean) => void;
  setLoadingProgress: (progress: number, phase: LoadingPhase) => void;
  setReady: (ready: boolean) => void;
  setError: (error: EngineError | null) => void;
  setAutoRotating: (rotating: boolean) => void;
  setMemoryUsage: (mb: number) => void;
  setAvailableColors: (colors: ResolvedColorDefinition[]) => void;
  setActiveColorId: (id: string | null) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Initial State (without actions)
// ---------------------------------------------------------------------------

const initialState = {
  loading: true,
  loadingProgress: 0,
  loadingPhase: 'critical' as LoadingPhase,
  isReady: false,
  lastError: null,
  autoRotating: false,
  memoryUsageMB: 0,
  availableColors: [] as ResolvedColorDefinition[],
  activeColorId: null as string | null,
};

// ---------------------------------------------------------------------------
// Store Factory
// ---------------------------------------------------------------------------

/**
 * Creates a new engine store instance.
 *
 * Each engine instance gets its own store — no global singletons.
 * This ensures multiple engines on one page don't interfere.
 */
export function createEngineStore() {
  return createStore<EngineStoreState>()(
    devtools(
      (set) => ({
        ...initialState,

        setLoading: (loading) => set({ loading }, false, 'setLoading'),

        setLoadingProgress: (progress, phase) =>
          set({ loadingProgress: progress, loadingPhase: phase }, false, 'setLoadingProgress'),

        setReady: (ready) =>
          set({ isReady: ready, loading: !ready }, false, 'setReady'),

        setError: (error) => set({ lastError: error }, false, 'setError'),

        setAutoRotating: (rotating) =>
          set({ autoRotating: rotating }, false, 'setAutoRotating'),

        setMemoryUsage: (mb) =>
          set({ memoryUsageMB: mb }, false, 'setMemoryUsage'),

        setAvailableColors: (colors) =>
          set({ availableColors: colors }, false, 'setAvailableColors'),

        setActiveColorId: (id) =>
          set({ activeColorId: id }, false, 'setActiveColorId'),

        reset: () => set(initialState, false, 'reset'),
      }),
      { name: 'IMS-Engine', enabled: import.meta.env.DEV },
    ),
  );
}

// ---------------------------------------------------------------------------
// Snapshot Helper
// ---------------------------------------------------------------------------

/** Extract a serialisable snapshot from the store (no functions). */
export function getSnapshot(
  store: ReturnType<typeof createEngineStore>,
): EngineStateSnapshot {
  const s = store.getState();
  return {
    loading: s.loading,
    loadingProgress: s.loadingProgress,
    loadingPhase: s.loadingPhase,
    isReady: s.isReady,
    lastError: s.lastError,
    autoRotating: s.autoRotating,
    memoryUsageMB: s.memoryUsageMB,
  };
}
